import type { CountryCode } from "@freightsimple/generated-dashboard-openapi-client/models/CountryCode";
import { PickupDatePicker } from "@freightsimple/ui-core/DateTime/Controls/PickupDatePicker";
import { asLocalDate } from "@freightsimple/time/branded-types";
import moment from "moment";
import { DateHolidayMessage } from "@/Shared/Dates/DateHolidayMessage";
import { pickupHolidaysOn } from "@/Shared/Dates/pickupHolidays";

interface ChangePickupDateInputProps {
  country?: CountryCode | undefined;
  currentPickupDate: string;
  date: string;
  onDateChange: (date: moment.Moment) => void;
}

/**
 * ui-core's PickupDatePicker takes holidays as a list of dates and names, since
 * it stays holiday-library-agnostic. Our holiday source is a predicate, so walk
 * the selectable window and name the days that hit. The window is at most about
 * five weeks (today to a week past the current pickup), so this is a handful of
 * checks rather than a scan.
 */
function holidaysInWindow(
  earliest: moment.Moment,
  latest: moment.Moment,
  country: CountryCode | undefined,
) {
  const holidays = [];
  const cursor = earliest.clone();
  while (!cursor.isAfter(latest)) {
    const date = asLocalDate(cursor.format("YYYY-MM-DD"));
    if (pickupHolidaysOn(date, country).length > 0) {
      holidays.push({
        date,
        name: <DateHolidayMessage date={cursor.clone()} country={country} />,
      });
    }
    cursor.add(1, "day");
  }
  return holidays;
}

export function ChangePickupDateInput(props: ChangePickupDateInputProps) {
  const earliest = moment().startOf("day");
  const latest = moment(props.currentPickupDate)
    .clone()
    .startOf("day")
    .add(1, "week");

  return (
    <PickupDatePicker
      value={asLocalDate(props.date)}
      onChange={function (date) {
        props.onDateChange(moment(date));
      }}
      earliestDate={asLocalDate(earliest.format("YYYY-MM-DD"))}
      latestDate={asLocalDate(latest.format("YYYY-MM-DD"))}
      holidays={holidaysInWindow(earliest, latest, props.country)}
      tooltips={{
        today:
          "⚠️ Note: For same day pickups, typically a carrier needs at least 3 hours notice to be able to pick up a shipment. If the carrier is unable to pick it up, they will pick it up on the next business day.",
        // Narrower than ui-core's default month-long window: moving an existing
        // pickup is only supported a week out.
        beyondLatest:
          "⚠️ FreightSimple only supports moving pickups within the next week. Please contact our support team if you need something else.",
      }}
    />
  );
}
